import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Group, Skeleton, Stack, Text } from '@mantine/core';
import PageTitle from '@/components/common/PageTitle';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconPlus, IconSearch, IconWheat } from '@tabler/icons-react';
import EmptyState from '@/components/common/EmptyState';
import BuyFilterBar from '@/components/app/buy/BuyFilterBar';
import BasisCard from '@/components/app/buy/BasisCard';
import BuyAttestationGate from '@/components/app/buy/BuyAttestationGate';
import { orderService } from '@/api/services/OrderService';
import { BUY_TREE_QUERY_KEY, useBuyTree } from '@/hooks/useBuy';
import { useProfile } from '@/hooks/useProfile';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useIsAgent } from '@/hooks/useRoles';
import { flattenBuyTree, groupByBasis, type BasisGroup } from '@/lib/buyTree';
import { makeGroupUrl } from '@/lib/buyLinks';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import type { PriceListGroup } from '@/types/buy';
import { formatOrgName } from '@/lib/format';

const basisKey = (group: { address: string; addressTitle: string }): string =>
  `${group.address}|${group.addressTitle}`;

// «Купить» — мои прайс-листы на покупку: карточки базисов (у каждого — ТОЛЬКО его реальные
// культуры с диапазонами цен; культура появляется фактом существования прайса — решение бизнеса,
// сводная матрица с пустыми клетками удалена). Срез по культуре — чипом-фильтром.
export default function BuyListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useBuyTree();
  const isAgent = useIsAgent(); // агент не публикует прайс-листы (только посредничает по сделкам)
  // Предвыбор фильтра из query (из «Лучшей цены» / возврата к списку) — фильтр переживает навигацию.
  const [params] = useSearchParams();
  const initAddress = params.get('address');
  const initAddressTitle = params.get('addressTitle');
  const [basis, setBasis] = useState<string | null>(
    initAddress && initAddressTitle ? `${initAddress}|${initAddressTitle}` : null,
  );
  const [good, setGood] = useState<string | null>(params.get('goods'));

  const groups = useMemo(() => flattenBuyTree(data), [data]);
  const allBases = useMemo(() => groupByBasis(groups), [groups]);
  // Опции фильтра — только базисы, в которых есть объявления о покупке (дерево других не содержит).
  const basisOptions = useMemo(
    () =>
      allBases.map((base) => ({
        value: basisKey(base),
        label: `${formatOrgName(base.addressTitle)} — ${base.address}`,
        title: base.addressTitle,
        address: base.address,
      })),
    [allBases],
  );
  const goodsOptions = useMemo(() => [...new Set(groups.map((group) => group.goods))].sort(), [groups]);

  const filtered = useMemo(
    () => groups.filter((group) => (!basis || basisKey(group) === basis) && (!good || group.goods === good)),
    [groups, basis, good],
  );
  const basisCards = useMemo(() => groupByBasis(filtered), [filtered]);
  const showFilters = allBases.length > 1 || goodsOptions.length > 1;

  function openGroup(group: PriceListGroup): void {
    navigate(makeGroupUrl(group));
  }

  // Создание прайс-листа: до аттестации недоступно (без неё бэкенд вернёт 423). Действие создания
  // не показываем вовсе, а сверху выводим баннер со ссылкой на Аттестацию; клик оставляем со
  // страховочным перенаправлением на случай прямого вызова.
  const { data: profile } = useProfile();
  const attestationRequired = useAttestationRequired();
  const startCreate = (): void => {
    if (profile && profile.isVerified === false) {
      notifications.show({ color: 'yellow', message: t('app.buy.form.needAttestation') });
      navigate(ROUTES.APP_COMPANY_ACCREDITATION);
      return;
    }
    navigate(ROUTES.APP_BUY_NEW);
  };

  // Удаление ВСЕХ прайс-листов базиса (деструктивно — подтверждение с явным охватом).
  const queryClient = useQueryClient();
  const removeBasis = useMutation({
    mutationFn: (basisGroup: BasisGroup) =>
      orderService.deletePurchaseByBasis({ address: basisGroup.address, addressTitle: basisGroup.addressTitle }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUY_TREE_QUERY_KEY });
      notifications.show({ color: 'lime', message: t('app.buy.del.deleted') });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(error)
          ? t('auth.errors.unavailable')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
      });
    },
  });

  function confirmDeleteBasis(basisGroup: BasisGroup): void {
    modals.openConfirmModal({
      title: t('app.buy.del.basisTitle'),
      children: <Text size="sm">{t('app.buy.del.basisConfirm', { basis: formatOrgName(basisGroup.addressTitle) })}</Text>,
      labels: { confirm: t('app.buy.del.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeBasis.mutate(basisGroup),
    });
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* wrap: на узком экране кнопки действий переносятся под заголовок, а не сжимают его в столбик. */}
        <Group justify="space-between" wrap="wrap" gap="md" align="flex-start">
          <Stack gap={4} style={{ flex: '1 1 260px', minWidth: 0 }}>
            <PageTitle>{t('app.buy.title')}</PageTitle>
            <Text size="sm" c="gray.8">
              {t('app.buy.subtitle')}
            </Text>
          </Stack>
          <Group gap="sm" wrap="wrap" style={{ flexShrink: 0, maxWidth: '100%' }}>
            {/* Поиск рынка связан с закупкой — держим вход в «Лучшую цену» прямо в разделе. */}
            <Button
              component={Link}
              to={ROUTES.APP_BEST_PRICE}
              variant="default"
              leftSection={<IconSearch size={16} />}
            >
              {t('app.nav.bestPrice')}
            </Button>
            {groups.length > 0 && !isAgent && !attestationRequired ? (
              <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
                {t('app.buy.newPriceList')}
              </Button>
            ) : null}
          </Group>
        </Group>

        {attestationRequired && !isAgent ? <BuyAttestationGate /> : null}

        {isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : isLoading ? (
          <Stack gap="sm">
            <Skeleton h={140} />
            <Skeleton h={140} />
          </Stack>
        ) : groups.length === 0 ? (
          <Card withBorder padding="lg">
            {isAgent ? (
              <EmptyState
                decorated={false}
                icon={<IconWheat size={30} />}
                title={t('app.buy.agentTitle')}
                description={t('app.buy.agentHint')}
              />
            ) : (
              <EmptyState
                decorated={false}
                icon={<IconWheat size={30} />}
                title={t('app.buy.empty')}
                description={t('app.buy.emptyHint')}
                action={
                  attestationRequired ? undefined : (
                    <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
                      {t('app.buy.create')}
                    </Button>
                  )
                }
              />
            )}
          </Card>
        ) : (
          <Stack gap="md">
            {showFilters ? (
              <Group justify="space-between" wrap="wrap" align="center" gap="sm">
                <BuyFilterBar
                  bases={basisOptions}
                  goods={goodsOptions}
                  basis={basis}
                  good={good}
                  onBasis={setBasis}
                  onGood={setGood}
                />
                <Text size="sm" c="gray.8">
                  {t('app.buy.bases', { count: basisCards.length })} ·{' '}
                  {t('app.buy.priceLists', { count: filtered.length })}
                </Text>
              </Group>
            ) : null}
            {basisCards.length === 0 ? (
              <Group gap="xs">
                <Text c="gray.7" size="sm">
                  {t('app.buy.nothingFound')}
                </Text>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => {
                    setBasis(null);
                    setGood(null);
                  }}
                >
                  {t('app.buy.resetFilters')}
                </Button>
              </Group>
            ) : (
              <Stack gap="md">
                {basisCards.map((basisGroup) => (
                  <BasisCard
                    key={basisKey(basisGroup)}
                    basis={basisGroup}
                    onOpen={openGroup}
                    onDelete={() => confirmDeleteBasis(basisGroup)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
